import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Settings as SettingsIcon,
  Webhook as WebhookIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import { AdminSectionPanel } from "@/components/common";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { Customer } from "@/services/features/StoreService";
import {
  CustomerWebhookCreateRequest,
  WebhookEvent,
  WebhookType,
} from "@/services/features/WebhookService";

export type WebhookFormData = CustomerWebhookCreateRequest & { enabled?: boolean };

export interface WebhookFormErrors {
  customerId?: string;
  name?: string;
  url?: string;
}

interface WebhookFormProps {
  data: WebhookFormData;
  errors: WebhookFormErrors;
  isEdit: boolean;
  isSubmitting: boolean;
  submitLabel: string;
  customers?: Customer[];
  selectedCustomer?: Customer | null;
  onCustomerChange?: (customer: Customer | null) => void;
  onFieldChange: (
    field: keyof WebhookFormData,
    value: WebhookFormData[keyof WebhookFormData],
  ) => void;
  onAddHeader: () => void;
  onRemoveHeader: (index: number) => void;
  onUpdateHeader: (index: number, field: "key" | "value", value: string) => void;
  onSubmit: () => void;
}

/** Shared create/edit webhook form layout; pages own data loading, validation, and API calls. */
export const WebhookForm: React.FC<WebhookFormProps> = ({
  data,
  errors,
  isEdit,
  isSubmitting,
  submitLabel,
  customers = [],
  selectedCustomer = null,
  onCustomerChange,
  onFieldChange,
  onAddHeader,
  onRemoveHeader,
  onUpdateHeader,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const eventOptions = [
    {
      value: WebhookEvent.REQUEST_CREATE,
      label: t("management.webhooks.webhooks.events.requestCreated"),
    },
    {
      value: WebhookEvent.REQUEST_UPDATE,
      label: t("management.webhooks.webhooks.events.requestUpdated"),
    },
    {
      value: WebhookEvent.ROOM_UPDATE,
      label: t("management.webhooks.webhooks.events.roomUpdated"),
    },
  ];
  const typeOptions = [
    {
      value: WebhookType.HTTPS,
      label: t("management.webhooks.createWebhook.options.https"),
    },
    {
      value: WebhookType.KAFKA,
      label: t("management.webhooks.createWebhook.options.kafka"),
    },
  ];

  return (
    <>
      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          <AdminSectionPanel
            title={t("management.webhooks.createWebhook.sections.webhookDetails")}
            icon={<WebhookIcon />}
          >
            <Stack spacing={3}>
              {!isEdit && onCustomerChange && (
                <MuiAutocompleteMultiSelect
                  label={t("management.webhooks.createWebhook.fields.customer")}
                  options={customers}
                  value={selectedCustomer ? [selectedCustomer] : []}
                  onChange={(next) => onCustomerChange(next[0] ?? null)}
                  getOptionLabel={(option) => option.name}
                  getOptionKey={(option) => option.id}
                  required
                  placeholder={t("management.webhooks.createWebhook.placeholders.selectCustomer")}
                  error={Boolean(errors.customerId)}
                  helperText={errors.customerId}
                />
              )}

              <TextField
                label={t("management.webhooks.createWebhook.fields.webhookName")}
                value={data.name}
                onChange={(event) => onFieldChange("name", event.target.value)}
                fullWidth
                required
                error={Boolean(errors.name)}
                helperText={errors.name}
                placeholder={t("management.webhooks.createWebhook.placeholders.enterWebhookName")}
              />

              <TextField
                label={t("management.webhooks.createWebhook.fields.webhookUrl")}
                value={data.url}
                onChange={(event) => onFieldChange("url", event.target.value)}
                fullWidth
                required
                error={Boolean(errors.url)}
                helperText={errors.url}
                placeholder={t("management.webhooks.createWebhook.placeholders.webhookUrl")}
                type="url"
              />

              <MuiAutocompleteMultiSelect
                label={t("management.webhooks.createWebhook.fields.eventType")}
                options={eventOptions}
                value={eventOptions.filter((option) => option.value === data.event)}
                onChange={(next) => {
                  const selected = next[0];
                  if (selected) onFieldChange("event", selected.value);
                }}
                getOptionLabel={(option) => option.label}
                getOptionKey={(option) => option.value}
                required
                disableSearch
              />

              <MuiAutocompleteMultiSelect
                label={t("management.webhooks.createWebhook.fields.webhookType")}
                options={typeOptions}
                value={typeOptions.filter((option) => option.value === data.type)}
                onChange={(next) => {
                  const selected = next[0];
                  if (selected) onFieldChange("type", selected.value);
                }}
                getOptionLabel={(option) => option.label}
                getOptionKey={(option) => option.value}
                required
                disableSearch
              />
            </Stack>
          </AdminSectionPanel>
        </Grid>

        <Grid item xs={12} lg={6}>
          <AdminSectionPanel
            title={t("management.webhooks.createWebhook.sections.requestConfiguration")}
            icon={<SettingsIcon />}
          >
            <Stack spacing={3}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <Typography variant="h6">
                    {t("management.webhooks.createWebhook.sections.customHeaders")}
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={onAddHeader}
                    variant="outlined"
                  >
                    {t("management.webhooks.createWebhook.actions.addHeader")}
                  </Button>
                </Box>

                {data.headers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    {t("management.webhooks.createWebhook.empty.noCustomHeaders")}
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {data.headers.map((header, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                          p: 2,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          bgcolor: "grey.50",
                        }}
                      >
                        <TextField
                          label={t("management.webhooks.createWebhook.fields.headerName")}
                          value={header.key}
                          onChange={(event) => onUpdateHeader(index, "key", event.target.value)}
                          placeholder={t(
                            "management.webhooks.createWebhook.placeholders.headerName",
                          )}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label={t("management.webhooks.createWebhook.fields.headerValue")}
                          value={header.value}
                          onChange={(event) => onUpdateHeader(index, "value", event.target.value)}
                          placeholder={t(
                            "management.webhooks.createWebhook.placeholders.headerValue",
                          )}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          onClick={() => onRemoveHeader(index)}
                          color="error"
                          size="small"
                          sx={{ mt: 0.5 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="h6">
                  {t(
                    isEdit
                      ? "management.webhooks.editWebhook.sections.webhookOptions"
                      : "management.webhooks.createWebhook.sections.dataOptions",
                  )}
                </Typography>

                {isEdit && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={Boolean(data.enabled)}
                        onChange={(event) => onFieldChange("enabled", event.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">
                          {t("management.webhooks.editWebhook.fields.enabled")}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t("management.webhooks.editWebhook.helperText.enableDisableWebhook")}
                        </Typography>
                      </Box>
                    }
                  />
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={data.includeData}
                      onChange={(event) => onFieldChange("includeData", event.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {t("management.webhooks.createWebhook.fields.includeData")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("management.webhooks.createWebhook.helperText.includeDetailedData")}
                      </Typography>
                    </Box>
                  }
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={data.includeDemoStores}
                      onChange={(event) => onFieldChange("includeDemoStores", event.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">
                        {t("management.webhooks.createWebhook.fields.includeDemoStores")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(
                          "management.webhooks.createWebhook.helperText.includeEventsFromDemoStores",
                        )}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </Stack>
          </AdminSectionPanel>
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 3 }}>
        <Button
          variant="contained"
          onClick={onSubmit}
          startIcon={isEdit ? <EditIcon /> : <AddIcon />}
          disabled={isSubmitting}
          size="large"
        >
          {isSubmitting ? <CircularProgress size={20} /> : submitLabel}
        </Button>
      </Box>
    </>
  );
};
